export const ILLUMINATE_APP_ID = '8b3eac1c-5150-448e-8d99-fb7b860e7e41';
export const ILLUMINATE_FOLDERNAME = 'Illuminate App';

// VE-25098: duration of the folder-view row exit animation. Shared so the CSS
// keyframe (CaseFolders styles `exitingListItem`) and the batch saga's
// pre-delete `delay` stay in sync — changing it here changes both.
export const MULTI_SELECT_EXIT_ANIMATION_MS = 450;

/**
 * VE-28429/VE-30022: how long the folder view may be left for an editor and
 * still have the columns it was showing — the request's file list, and the
 * subfolder listing of each folder above it — put straight back on return
 * instead of being refetched.
 *
 * VE-30022 re-based this window. It used to run from when the list LOADED,
 * which measured the wrong thing: a request read for two minutes before a file
 * was opened was already outside it, so a ten-second look at a document still
 * cost a full reload, and browsing several files in a row reloaded on all but
 * the first exit. It now measures the trip itself — how long the user has been
 * away from the folder view — so a short trip never costs a reload however long
 * they had been sitting in the request beforehand.
 *
 * Deliberately short. The window trades "the list may be up to this old" for
 * not reloading a large request every time a file is opened and closed, and
 * only the second of those is reversible by the user — the refresh button is
 * right there. Start cautious; raise it once the shorter window has proven
 * itself in real requests.
 *
 * Distinct from `caseContentsLoadedAt`/`subFoldersLoadedAt` (see ./store),
 * which still record when each list genuinely came off the server. Those are
 * NOT what this gate reads any more; they are kept as the real-load record a
 * background refresh can be driven from.
 */
export const FOLDER_VIEW_AWAY_REUSE_MS = 60_000;

/**
 * The one rule that governs both halves of the reuse — the saga's fetch skip
 * and the reducer's "keep the rows on screen" branch. Keeping it in one place
 * is what stops those two disagreeing and painting a snapshot the fetch skip
 * then declines to correct.
 *
 * A folder qualifies only if it was one of the columns ON SCREEN when the
 * folder view was left. That scoping is what keeps the window honest: without
 * it, any folder whose rows happened to still be in state would be reused for
 * `FOLDER_VIEW_AWAY_REUSE_MS` after ANY editor entry — including one the user
 * reached by searching their way to a different request entirely, whose list
 * could be arbitrarily old.
 *
 * Structurally typed rather than importing `FolderViewTrip`, so the constants
 * module stays free of module-level imports from the store.
 */
export const canReuseAfterTrip = (
  trip: { leftAt: number; folderIds: string[] } | null | undefined,
  folderId: string | undefined
): boolean =>
  !!trip &&
  !!folderId &&
  trip.folderIds.includes(folderId) &&
  Date.now() - trip.leftAt < FOLDER_VIEW_AWAY_REUSE_MS;
